import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import {
  AdoptAdoptablesClickBiEvent,
  BiData,
  EditSaleAdoptablesClickBiEvent,
  MakeOfferAdoptablesClickBiEvent,
  ManageOfferAdoptablesClickBiEvent,
  ViewOffersAdoptablesClickBiEvent,
  OfferForSaleClickBiEvent,
  SellYourArtClickBiEvent,
  GiveAdoptableGiftClickBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { Contextify } from '@wix/da-bi/pkg/events/context';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import {
  pushModal,
  pushAdoptsBuyerModal,
  pushAdoptsSellerModal,
  pushAdoptsSellerGiftModal,
} from '../../../../Modals/redux/actionCreators';
import { ModalType } from '../../../../Modals/redux/types';
import {
  ExclusiveCategory,
  getExclusiveCategoryByNecoid,
  necoidToExclusiveCategory,
} from '../../../../types/exclusives';
import { useRestrictToLoggedInAndVerified } from '../../../../utils/hooks/useRestrictToLoggedInAndVerified';
import useAdoptableState, { UseAdoptableStateReturnType } from '../AdoptableInfo/useAdoptableState';

// TODO: Move these useXAction hooks somewhere central. They're used in multiple places that aren't the sidebar

/**
 * Returns a callback that's to be added to a "Buy it Now" button. Sends the user to checkout.
 *
 * Right now, only adoptables are supported.
 */
export function useBuyNowAction(
  deviation: PapiDeviation,
  biData: Omit<AdoptAdoptablesClickBiEvent, 'evid' | 'itemid' | 'typeid' | 'is_gift'>,
  foruserid?: number
) {
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();

  const buyNowAction = useCallback(() => {
    window.location.assign(
      Url.checkoutLink(deviation.extended?.adoptable?.fixedPrice?.subproductId ?? 0, foruserid)
    );
  }, [deviation, foruserid]);

  return [
    restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, buyNowAction),
    BiData<AdoptAdoptablesClickBiEvent>({
      evid: 750,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      is_gift: 0,
      ...biData,
    }),
  ];
}

/**
 * Buy adoptable and gift it to someone
 */
export function useGiftNowAction(
  deviation: PapiDeviation,
  biData: Omit<GiveAdoptableGiftClickBiEvent, 'evid' | 'itemid' | 'typeid'>,
  foruserid?: number
): [() => void, Contextify<GiveAdoptableGiftClickBiEvent>] {
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();

  const giftNowAction = useCallback(() => {
    window.location.assign(
      Url.checkoutLink(deviation.extended?.adoptable?.fixedPrice?.subproductId ?? 0, foruserid)
    );
  }, [deviation, foruserid]);

  return [
    restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, giftNowAction),
    BiData<GiveAdoptableGiftClickBiEvent>({
      evid: 757,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      sectionname: BiContextProvidedValue,
      is_seller_gift: 0,
      ...biData,
    }),
  ];
}

/**
 * Returns a callback that's to be added to a "Give as a Gift" button. Triggers an seller give modal.
 *
 * Right now, only adoptables are supported.
 */
export function useSellerGiveAsGift(
  deviation: PapiDeviation,
  biData: Omit<
    GiveAdoptableGiftClickBiEvent,
    'evid' | 'itemid' | 'typeid' | 'is_gift' | 'purchase_price'
  >
): [() => void, Contextify<GiveAdoptableGiftClickBiEvent>] {
  const { coreLevel } = useContext(SessionContext);
  const { freeGiftsRemaining = 0 } = useAdoptableState(deviation);
  const showGivePopup = useAction(pushAdoptsSellerGiftModal);

  const showUpgradePopupAction = useCallback(
    () =>
      pushModal(ModalType.GIFT_EXCLUSIVE_UPSELL, {
        params: { deviation },
      }),
    [deviation]
  );
  const showUpgradePopup = useAction(showUpgradePopupAction);

  const onClick = useCallback(() => {
    switch (coreLevel) {
      case 'core_plus':
      case 'core_pro':
      case 'core_pro_plus':
      case 'core_max':
        // check if freeGiftsRemaining exceeded
        if (freeGiftsRemaining <= 0) {
          showUpgradePopup();
          return;
        }
        showGivePopup(deviation);
        break;
      case 'core_access':
      default:
        // regular and core access users
        showUpgradePopup();
        break;
    }
  }, [deviation, freeGiftsRemaining, coreLevel, showGivePopup, showUpgradePopup]);

  return [
    onClick,
    BiData<GiveAdoptableGiftClickBiEvent>({
      evid: 757,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      sectionname: BiContextProvidedValue,
      purchase_price: 0,
      is_seller_gift: 1,
      ...biData,
    }),
  ];
}

/**
 * Returns a callback that's to be added to a "Make Offer" button. Triggers an offer modal.
 *
 * Right now, only adoptables are supported.
 */
export function useMakeOfferAction(
  deviation: PapiDeviation,
  biData: Omit<MakeOfferAdoptablesClickBiEvent, 'evid' | 'itemid' | 'typeid'>
): [() => void, Contextify<MakeOfferAdoptablesClickBiEvent>] {
  const dispatchPushAdoptsBuyerModal = useAction(pushAdoptsBuyerModal);
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();

  const makeOfferAction = useCallback(() => {
    dispatchPushAdoptsBuyerModal(deviation, 'offer');
  }, [deviation, dispatchPushAdoptsBuyerModal]);

  return [
    restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, makeOfferAction),
    BiData<MakeOfferAdoptablesClickBiEvent>({
      evid: 751,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      ...biData,
    }),
  ];
}

export function useManageOfferAction(
  deviation: PapiDeviation,
  biData: Omit<ManageOfferAdoptablesClickBiEvent, 'evid' | 'itemid' | 'typeid'>
): [() => void, Contextify<ManageOfferAdoptablesClickBiEvent>] {
  const [makeOfferAction] = useMakeOfferAction(deviation, { sectionname: '' });

  return [
    makeOfferAction,
    BiData<ManageOfferAdoptablesClickBiEvent>({
      evid: 753,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      ...biData,
    }),
  ];
}

export function useViewOffersAction(
  deviation: PapiDeviation,
  biData: Omit<ViewOffersAdoptablesClickBiEvent, 'evid' | 'itemid' | 'typeid'>
): [() => void, Contextify<ViewOffersAdoptablesClickBiEvent>] {
  const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
  const dispatchPushAdoptsBuyerModal = useAction(pushAdoptsBuyerModal);
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();

  const viewOffersAction = useCallback(() => {
    if (deviation.extended?.adoptable?.isOwner) {
      dispatchPushAdoptsSellerModal([deviation], 'offers');
    } else {
      dispatchPushAdoptsBuyerModal(deviation, 'view-offers');
    }
  }, [deviation, dispatchPushAdoptsSellerModal, dispatchPushAdoptsBuyerModal]);

  return [
    restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, viewOffersAction),
    BiData<ViewOffersAdoptablesClickBiEvent>({
      evid: 746,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      ...biData,
    }),
  ];
}

export function useSellAction(
  deviation: PapiDeviation
): [() => void, Contextify<SellYourArtClickBiEvent>] {
  const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();

  const sellAction = useCallback(() => {
    dispatchPushAdoptsSellerModal([deviation], 'sell-splash');
  }, [deviation, dispatchPushAdoptsSellerModal]);

  return [
    restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, sellAction),
    BiData<SellYourArtClickBiEvent>({
      evid: 353,
      deviationid: deviation.deviationId,
    }),
  ];
}

export function useResellAction(
  deviation: PapiDeviation,
  adoptableState: UseAdoptableStateReturnType
): [() => void, Contextify<OfferForSaleClickBiEvent>] {
  const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();

  const resellAction = useCallback(() => {
    dispatchPushAdoptsSellerModal([deviation], 'sell-adoptable');
  }, [deviation, dispatchPushAdoptsSellerModal]);

  return [
    adoptableState.isResellOptionEnabled &&
      restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, resellAction),
    BiData<OfferForSaleClickBiEvent>({
      evid: 739,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      necoid: deviation.extended?.adoptable?.necoId,
    }),
  ];
}

/**
 * Will perform either of our standard Sell or Resell actions. Sell action is returned when
 * the exclusive has never been sold. Resell is returned when the exclusive has been sold before.
 */
export function useSellOrResellAction(
  deviation: PapiDeviation
): [() => void, Contextify<OfferForSaleClickBiEvent> | Contextify<SellYourArtClickBiEvent>] {
  const adoptableState = useAdoptableState(deviation);
  const sellActionAndBiData = useSellAction(deviation);
  const resaleActionAndBiData = useResellAction(deviation, adoptableState);

  return adoptableState.saleCount > 0 ? resaleActionAndBiData : sellActionAndBiData;
}

export function useEndSaleAction(
  deviation: PapiDeviation,
  biData: Omit<EditSaleAdoptablesClickBiEvent, 'evid' | 'itemid' | 'typeid'>
): [() => void, Contextify<EditSaleAdoptablesClickBiEvent>] {
  const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
  const endSaleAction = useCallback(() => {
    dispatchPushAdoptsSellerModal([deviation], 'end-confirm');
  }, [dispatchPushAdoptsSellerModal, deviation]);

  return [
    endSaleAction,
    BiData<EditSaleAdoptablesClickBiEvent>({
      evid: 742,
      itemid: deviation.deviationId,
      typeid: deviation.typeId,
      ...biData,
    }),
  ];
}

export function useExclusiveCategoryTranslation(deviation: PapiDeviation) {
  const { t } = useTranslation();
  if (deviation.isOriginalsDelivery) {
    return t('common.exclusiveCategoryName.originals');
  }
  if (!deviation.extended?.adoptable?.necoId) {
    return t('common.exclusiveCategoryName.unknown');
  }

  switch (
    getExclusiveCategoryByNecoid(
      deviation.extended.adoptable.necoId.toString() as keyof typeof necoidToExclusiveCategory
    )
  ) {
    case ExclusiveCategory.Adoptable:
      return t('common.exclusiveCategoryName.adoptable');
    case ExclusiveCategory.DigitalArt:
      return t('common.exclusiveCategoryName.digitalArt');
    case ExclusiveCategory.GameArt:
      return t('common.exclusiveCategoryName.gameArt');
    case ExclusiveCategory.MixedMedia:
      return t('common.exclusiveCategoryName.mixedMedia');
    case ExclusiveCategory.Photography:
      return t('common.exclusiveCategoryName.photography');
    case ExclusiveCategory.Pinups:
      return t('common.exclusiveCategoryName.pinups');
    case ExclusiveCategory.TraditionalArt:
      return t('common.exclusiveCategoryName.traditionalArt');
    case ExclusiveCategory.ThreeDArt:
      return t('common.exclusiveCategoryName.3DArt');
    default:
      return t('common.exclusiveCategoryName.unknown');
  }
}
